import { Grid, Box } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useEffect, memo } from "react";
import {
  GetLocationQuery,
  UpdateLocationInput,
  useUpdateLocationMutation,
} from "src/generated/graphql";
import ImageUpload from "src/components/ImageUpload";
import { useParams } from "react-router-dom";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import { useDisclosure } from "@mantine/hooks";
import {
  locInitialValues,
  locationFormSchema,
} from "../../LocationList/CreateLocationForm";
import FormCard from "src/components/FormCard";
import {
  CardRadius,
  EMPLOYEE_ROLE_OBJ,
  HeightCard,
  MESSAGES,
  PATH_NAMES,
  TIMEZONES,
  Hours,
} from "src/utils/constants";
import { GetLocation } from "src/api/graphql/queries/Location";
import ActionCard from "src/components/ActionCard";
import TaxRate from "./TaxRate";
import Select from "src/components/Select";
import useWritePermission from "src/hooks/usePermission";
import { useAppContext } from "src/api/AppContext";

const locInitialValuesUpdate = {
  ...locInitialValues,
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  timezone: "",
};

type IFormProps = Omit<UpdateLocationInput, "primaryContact"> & {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
};

type ILocationDetail = {
  locData: GetLocationQuery | undefined;
};

const LocationDetail = ({ locData }: ILocationDetail) => {
  const { currentLocation, setCurrentLocation } = useAppContext();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgLocations);
  const { locId } = useParams<{ locId: string }>();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);

  const currentLocationId =
    locId === PATH_NAMES.GENERAL_LOC ? currentLocation?.id : locId;

  const [updateLocation, { loading }] = useUpdateLocationMutation({
    onCompleted: (data) => {
      toggleFormActive();
      form.resetDirty();
    },
    refetchQueries: [
      { query: GetLocation, variables: { id: currentLocationId } },
    ],
  });

  useEffect(() => {
    currentLocationId && resetFormState();
  }, [locData]);

  const form = useForm<UpdateLocationInput>({
    initialValues: locInitialValuesUpdate,
    validate: yupResolver(locationFormSchema),
  });
  const handleUpdate = async (values: IFormProps) => {
    if (!currentLocationId) return;
    const {
      address,
      zip,
      name,
      description,
      city,
      state,
      firstName,
      lastName,
      email,
      phoneNumber,
      logoImage,
      timezone,
      notificationSenderNumber,
      transactionProcessTime,
      stationsEnabled,
    } = values;
    try {
      await updateLocation({
        variables: {
          data: {
            address,
            notificationSenderNumber,
            transactionProcessTime,
            logoImage,
            zip,
            name,
            description,
            city,
            state,
            timezone,
            // stationsEnabled,
            primaryContact: { firstName, lastName, email, phoneNumber },
          },
          id: currentLocationId,
        },
      });
      if (currentLocation?.id === currentLocationId) {
        setCurrentLocation({
          id: currentLocationId,
          name: name!,
          description,
          logoUrl: logoImage,
          // stationsEnabled: stationsEnabled,
        });
      }
    } catch (e: any) {
      console.error("Error: ", e.response);
    }
  };

  const resetFormState = () => {
    const {
      logoUrl: logoImage,
      primaryContact,
      state,
      ...rest
    } = locData?.locationOne || {};
    const { firstName, lastName, email, phoneNumber } = primaryContact || {};
    let _data = {
      logoImage,
      state: state || "",
      firstName: firstName || "",
      lastName: lastName || "",
      email: email || "",
      phoneNumber: phoneNumber || "",
      ...rest,
    };
    form.setValues(_data);
    form.resetDirty(_data);
  };

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleCancel = () => {
    resetFormState();
    toggleFormActive();
  };

  const isFormUpdated = !isFormActive && form.isDirty() && form.isTouched();
  return (
    <>
      <FormCard
        title={"Basic Info"}
        loading={loading}
        bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
        onCancel={handleCancel}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) => handleUpdate(values))}
        isFormActive={isFormActive}
      >
        <ActionCard
          title="General"
          my={10}
          height={HeightCard.sm}
          radius={CardRadius.sm}
          size={"sm"}
        >
          <Box mx={18}>
            <Grid gutter={18} m={0}>
              <Grid.Col span={12} lg={5}>
                <div style={{ margin: "auto" }}>
                  <ImageUpload
                    onReadSuccess={(b64) =>
                      form.setFieldValue("logoImage", b64)
                    }
                    src={form.values.logoImage!}
                    alt="location Logo"
                    sx={{ minHeight: 275 }}
                    disabled={isFormActive}
                  />
                </div>
              </Grid.Col>
              <Grid.Col span={12} lg={7}>
                <TextInput
                  mt={0}
                  withAsterisk
                  label="Name"
                  {...form.getInputProps("name")}
                  disabled={isFormActive}
                />
                <Textarea
                  label="Description"
                  {...form.getInputProps("description")}
                  disabled={isFormActive}
                />
                <TextInput
                  label="Address"
                  {...form.getInputProps("address")}
                  disabled={isFormActive}
                />
                <TextInput
                  label="City"
                  {...form.getInputProps("city")}
                  disabled={isFormActive}
                />
                <TextInput
                  label="State"
                  {...form.getInputProps("state")}
                  disabled={isFormActive}
                />
                <TextInput
                  label="Zip"
                  {...form.getInputProps("zip")}
                  disabled={isFormActive}
                />
                <TextInput
                  label="Sender Phone Number"
                  maxLength={11}
                  {...form.getInputProps("notificationSenderNumber")}
                  disabled={isFormActive}
                />
                <Select
                  label="Timezone"
                  data={TIMEZONES.map((item) => ({
                    value: item.value,
                    label: item.name,
                  }))}
                  {...form.getInputProps("timezone")}
                  disabled={isFormActive}
                />

                <Select
                  label="Transaction Process Time"
                  data={Hours.map((item) => ({
                    value: item.value,
                    label: item.name,
                  }))}
                  {...form.getInputProps("transactionProcessTime")}
                  disabled={isFormActive}
                />

                {/* <Switch
                  labelPosition="left"
                  label="Enable Time Clock Stations"
                  checked={form.getInputProps("stationsEnabled").value}
                  {...form.getInputProps("stationsEnabled")}
                  disabled={isFormActive}
                  mt="md"
                /> */}
              </Grid.Col>
            </Grid>
          </Box>
        </ActionCard>

        <ActionCard
          title="Primary Contact"
          mb={10}
          height={HeightCard.sm}
          radius={CardRadius.sm}
          size={"sm"}
        >
          <Box mx={18}>
            <Grid gutter={18} m={0}>
              <Grid.Col span={6}>
                <TextInput
                  mt={0}
                  label="First Name"
                  {...form.getInputProps("firstName")}
                  disabled={isFormActive}
                />
                <TextInput
                  label="Last Name"
                  {...form.getInputProps("lastName")}
                  disabled={isFormActive}
                />
              </Grid.Col>

              <Grid.Col span={6}>
                <TextInput
                  mt={0}
                  label="Email"
                  {...form.getInputProps("email")}
                  disabled={isFormActive}
                />
                <TextInput
                  label="Phone"
                  {...form.getInputProps("phoneNumber")}
                  disabled={isFormActive}
                />
              </Grid.Col>
            </Grid>
          </Box>
        </ActionCard>
      </FormCard>

      <Box mt={"sm"} mb={"xs"}>
        <TaxRate isAllowWrite={isAllowWrite} />
      </Box>
    </>
  );
};

export default memo(LocationDetail);
